import { AfterViewInit, ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { TaskAttachedEntityType, TaskSearchInput } from '../../../../generated/generated-types';
import { TaskListWidgetComponent } from '../task-list-widget/task-list-widget.component';

type ComponentFieldProps = {
    attachedEntityType?: string;
    attachedEntityId?: string;
    searchInput?: Partial<TaskSearchInput>;
};

@Component({
    selector: 'app-formly-task-list-widget',
    templateUrl: './formly-task-list-widget.component.html',
    styleUrls: ['./formly-task-list-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    host: {
        '[class.phx-base-list--content-height]': 'isContentHeightList',
    },
    imports: [TaskListWidgetComponent],
})
export class FormlyTaskListWidgetComponent extends FieldType<FieldTypeConfig> implements OnInit, AfterViewInit {

    private readonly destroyRef = inject(DestroyRef);

    readonly taskListWidget = viewChild(TaskListWidgetComponent);

    readonly attachedEntityType = signal<TaskAttachedEntityType | undefined>(undefined);
    readonly attachedEntityId = signal<string | undefined>(undefined);

    /** True when the tab opts out of fill-height (detail `.content` scrolls instead). */
    get isContentHeightList(): boolean {
        return this.props?.fillHeight === false;
    }

    /** When `props.fillHeight === false`, list sizes to rows; detail `.content` scrolls. */
    get fillHeightList(): boolean {
        return !this.isContentHeightList;
    }

    readonly searchInput = computed<Partial<TaskSearchInput>>(() => {
        const fieldProps = this.field.props as ComponentFieldProps;
        return {
            ...fieldProps.searchInput,
            attachedEntityType: this.attachedEntityType(),
            attachedEntityId: this.attachedEntityId(),
        };
    });

    ngOnInit(): void {
        const fieldProps = this.field.props as ComponentFieldProps;

        this.attachedEntityType.set(fieldProps.attachedEntityType as TaskAttachedEntityType);

        if (fieldProps.attachedEntityId) {
            this.attachedEntityId.set(fieldProps.attachedEntityId);
            return;
        }

        if (!this.field.key || !this.formControl) {
            return;
        }

        this.attachedEntityId.set(this.formControl.value);
        this.formControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                this.attachedEntityId.set(value);
                this.applyFilter();
            });
    }

    ngAfterViewInit(): void {
        this.applyFilter();
    }

    private applyFilter(): void {
        const widget = this.taskListWidget();
        const input = this.searchInput();
        if (widget && input.attachedEntityType && input.attachedEntityId) {
            widget.setCustomSearchInput(input);
        }
    }
}
